/**
 * Desinstalação do agente (`uninstall_agent`) — puro, sem React, para o check
 * de contrato.
 *
 * Revogar licença, remover máquina ou remover empresa faz o ATHENA enfileirar a
 * desinstalação sozinho (README do Athena §4.8): o Hermes só avisa antes, mostra
 * o andamento e deixa o administrador reemitir. Como em todo comando, aceito não
 * é feito: só `executed` quer dizer que o agente começou a se desinstalar (e o
 * Athena o revogou).
 */
import type { Tone } from '../../components/primitives/Metric.tsx'
import type { AgentCommand } from '../api/admin.ts'
import { isAwaitingAgent } from './semantics.ts'

export const UNINSTALL_KIND = 'uninstall_agent'

/** O que disparou a desinstalação. Aberto: valor novo vai como veio. */
export type UninstallTrigger =
  | 'license_revoked'
  | 'machine_deleted'
  | 'company_deleted'
  | 'operator'
  | (string & {})

/**
 * `trigger` de `params`, que chega como STRING JSON (`'{"trigger":"…"}'`).
 * `null` quando falta, não é JSON ou não é texto — nunca lança.
 */
export function uninstallTriggerOf(command: { params?: string | null }): UninstallTrigger | null {
  if (command.params == null || command.params === '') return null
  try {
    const parsed: unknown = JSON.parse(command.params)
    if (parsed === null || typeof parsed !== 'object') return null
    const trigger = (parsed as Record<string, unknown>).trigger
    return typeof trigger === 'string' && trigger !== '' ? trigger : null
  } catch {
    return null
  }
}

const TRIGGER_LABEL: Record<string, string> = {
  license_revoked: 'Licença revogada',
  machine_deleted: 'Máquina removida',
  company_deleted: 'Empresa removida',
  operator: 'Pedido de administrador',
}

export function uninstallTriggerLabel(trigger: string | null): string {
  if (trigger === null) return 'Gatilho não informado'
  return TRIGGER_LABEL[trigger] ?? trigger
}

/** O selo da máquina. "Agente desinstalado" só em `executed`. */
export function uninstallBadge(state: string): { label: string; tone: Tone; hint: string } {
  switch (state) {
    case 'pending':
      return {
        label: 'Desinstalação pendente',
        tone: 'accent',
        hint: 'Na fila, aguardando o agente (até 30 dias).',
      }
    case 'delivered':
      return {
        label: 'Desinstalação pendente',
        tone: 'warn',
        hint: 'Entregue ao agente, SEM confirmação.',
      }
    case 'executed':
      return {
        label: 'Agente desinstalado',
        tone: 'ok',
        hint: 'Agente desinstalado e revogado.',
      }
    case 'failed':
      return {
        label: 'Desinstalação falhou',
        tone: 'crit',
        hint: 'O agente recusou antes de mexer em qualquer coisa e segue instalado.',
      }
    case 'expired':
      return {
        label: 'Desinstalação expirada',
        tone: 'warn',
        hint: '30 dias sem a máquina aparecer. O agente segue instalado nela.',
      }
    case 'cancelled':
      return {
        label: 'Desinstalação cancelada',
        tone: 'neutral',
        hint: 'Um administrador cancelou. O agente segue instalado.',
      }
    case 'skipped':
      // Não verificado: o contrato não diz quando o Argos responde `skipped` aqui.
      return {
        label: 'Desinstalação sem efeito',
        tone: 'neutral',
        hint: 'O agente respondeu que não havia o que fazer.',
      }
    default:
      return {
        label: `Desinstalação: ${state}`,
        tone: 'neutral',
        hint: `Estado "${state}" desconhecido.`,
      }
  }
}

/**
 * O que a tela pode AFIRMAR depois de uma desinstalação. Escrito aqui, e não no
 * JSX, para o check provar que só `executed` vira "desinstalado".
 */
export function uninstallOutcome(state: string): { tone: Tone; headline: string; uninstalled: boolean } {
  switch (state) {
    case 'pending':
      return {
        tone: 'accent',
        headline:
          'Na fila, aguardando o agente (até 30 dias). O Argos segue instalado.',
        uninstalled: false,
      }
    case 'delivered':
      return {
        tone: 'warn',
        headline: 'Entregue ao agente, sem confirmação. O Argos segue instalado.',
        uninstalled: false,
      }
    case 'executed':
      return {
        tone: 'ok',
        headline:
          'Agente desinstalado e revogado.',
        uninstalled: true,
      }
    case 'failed':
      return {
        tone: 'crit',
        headline: 'A desinstalação FALHOU: o agente segue instalado.',
        uninstalled: false,
      }
    case 'expired':
      return {
        tone: 'warn',
        headline: 'Expirou: 30 dias sem a máquina aparecer. O agente segue instalado.',
        uninstalled: false,
      }
    case 'cancelled':
      return {
        tone: 'neutral',
        headline: 'Desinstalação cancelada por um administrador. O agente segue instalado.',
        uninstalled: false,
      }
    case 'skipped':
      return {
        tone: 'neutral',
        headline: 'O agente respondeu que não havia o que desinstalar.',
        uninstalled: false,
      }
    default:
      return {
        tone: 'neutral',
        headline: `Estado "${state}" desconhecido.`,
        uninstalled: false,
      }
  }
}

/** Há uma desinstalação aberta: acompanhar, nunca duplicar. */
export function isUninstallOpen(command: { state: string } | null): boolean {
  return command !== null && isAwaitingAgent(command.state)
}

/** A última falhou ou venceu: o caso em que reemitir é o próximo passo natural. */
export function needsReissue(command: { state: string } | null): boolean {
  return command !== null && (command.state === 'failed' || command.state === 'expired')
}

/**
 * A desinstalação mais recente de cada máquina, de uma página da frota ordenada
 * do mais novo para o mais antigo (`GET /api/admin/commands`).
 */
export function latestUninstallByEndpoint(items: readonly AgentCommand[]): Map<string, AgentCommand> {
  const latest = new Map<string, AgentCommand>()
  for (const command of items) {
    if (command.kind !== UNINSTALL_KIND) continue
    const seen = latest.get(command.endpoint_id)
    if (seen === undefined || command.requested_at > seen.requested_at) latest.set(command.endpoint_id, command)
  }
  return latest
}

// ── avisos dos diálogos de revogar e remover ────────────────────────────────

const IRREVERSIBLE =
  'É irreversível.'

/**
 * Aviso de revogar/remover licença. `null` sem máquina vinculada (não há o que
 * desinstalar). `otherLiveKey`: `true` se a tela VIU outra licença viva da
 * máquina, `false` se sabe que não há, `null` se não sabe.
 */
export function licenseUninstallWarning(hasMachine: boolean, otherLiveKey: boolean | null): string | null {
  if (!hasMachine) return null
  if (otherLiveKey === true) {
    return 'A máquina vinculada tem outra licença viva: ela continua cliente e o Argos dela NÃO será desinstalado.'
  }
  const base = `O Argos da máquina vinculada se desinstala e apaga os dados locais. ${IRREVERSIBLE}`
  return otherLiveKey === false
    ? base
    : `${base} Se a máquina tiver outra licença viva, ela continua cliente e o agente não é desinstalado.`
}

/**
 * Toast de revogar/remover licença: diz que a desinstalação vai acontecer, nunca
 * que aconteceu. O 200/204 não informa se havia agente ativo para receber.
 */
export function licenseUninstallToast(done: string, hasMachine: boolean, otherLiveKey: boolean | null): string {
  if (!hasMachine || otherLiveKey === true) return done
  return otherLiveKey === false
    ? `${done} O agente da máquina vai se desinstalar quando conectar.`
    : `${done} Sem outra licença viva, o agente da máquina vai se desinstalar quando conectar.`
}

export const MACHINE_UNINSTALL_WARNING = `O Argos desta máquina se desinstala e apaga os dados locais. ${IRREVERSIBLE}`

/**
 * Sem a quantidade: `CompanyResponse` não traz contagem de máquinas, e o aviso
 * não justifica uma chamada a mais.
 */
export const COMPANY_UNINSTALL_WARNING =
  'Todas as máquinas da empresa com agente ativo terão o Argos desinstalado, com os dados locais apagados. Irreversível.'
